import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import { cn } from "../lib/cn";
import { fieldWidthClass } from "../lib/field-width";
/**
 * The shared control skin, WITHOUT a height floor.
 *
 * Input, Textarea and the Select trigger all wear it, which is the only reason
 * a form of mixed controls lines up: same border, same radius, same focus
 * treatment. Import it rather than re-typing the classes — a copy is how the
 * three drift apart.
 *
 * Split out from `controlClasses` because `Textarea` needs the skin with a
 * DIFFERENT floor (`min-h-20`). The floor is `h-control`, a plain unlayered CSS
 * class (see `styles/primitives.css`), so tailwind-merge cannot see it as
 * conflicting with `min-h-20` and the unlayered rule would have won — a textarea
 * collapsed to the height of a single-line input. Wearing the skin without the
 * floor is what keeps that honest.
 *
 * Error styling is driven by `aria-invalid`, not by a prop: the attribute the
 * screen reader needs is the same attribute that paints the border, so the two
 * cannot disagree.
 */
export const controlSkinClasses = [
    "flex w-full rounded-control",
    "border border-border-strong bg-surface shadow-input",
    "px-3 py-2 text-sm text-primary",
    "placeholder:text-placeholder",
    "transition-colors",
    "focus-visible:outline-none focus-visible:shadow-focus",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "aria-invalid:border-danger",
].join(" ");
/**
 * The height floor every interactive control shares.
 *
 * `h-control` resolves `--control-height`, which is 44px until an app opts into
 * a density on `AppShell` (docs/018-ux-standards.md, "Density"). It replaced a
 * hardcoded `min-h-11`, which applied kiosk touch sizing to the desktop admin
 * console too.
 */
export const controlHeightClass = "h-control";
/** The full skin: `controlSkinClasses` plus the shared height floor. */
export const controlClasses = `${controlSkinClasses} ${controlHeightClass}`;
/**
 * The MERGED control box — one bordered field holding two controls.
 *
 * `TimeField` (a time text field + a quick-pick chevron) and `PhoneInput` (a
 * country trigger + a number field) each edit ONE value through TWO elements
 * with two accessible names. Painting them as two bordered boxes read as two
 * fields; these three constants paint them as one. Built once here, beside the
 * skin they derive from, so the two composites cannot drift from each other or
 * from a plain `Input` — the repo rule that a control pattern is built once in
 * `packages/ui` (docs/018).
 *
 * The HEIGHT contract is the whole point, and the reason this is not just
 * `controlClasses` on a `<div>`. A plain `Input` is `max(--control-height,
 * 20px line + 2 × py-2 + 2px border)`. The first version of the merged box
 * wore `controlClasses` (so `py-2` on the wrapper) AND left `h-control` on the
 * inner `SearchableSelect` trigger — a full control-height child inside a
 * padded box, so the box came out `44 + 16 + 2 = 62px` beside a 44px date
 * input. Overriding the child with `min-h-0` / `h-full` cannot work: `h-control`
 * is an UNLAYERED rule (`styles/primitives.css`) and beats every Tailwind
 * utility. So the structure is inverted instead:
 *
 * - the WRAPPER owns border, surface, radius, the `h-control` floor — and NO
 *   padding (`mergedControlClasses`);
 * - the TEXT part owns the `px-3 py-2` a plain `Input` has, so its 20px line
 *   plus padding plus the wrapper's border is exactly the `Input` formula above,
 *   at every density (`mergedControlTextClasses`);
 * - the TRIGGER part carries no floor and no vertical padding and stretches to
 *   the wrapper's full inner height (`mergedControlTriggerClasses`), so it can
 *   never be the tallest thing in the row, and its click target is the whole
 *   height of the box rather than the height of its chevron. `SearchableSelect`
 *   wears it via `triggerVariant="embedded"`, which drops `controlClasses` —
 *   and with it `h-control` — rather than trying to out-specify it.
 *
 * Focus and error, which a `<div>` cannot express with `focus-visible:` or
 * `aria-invalid:` (it is never focused and never invalid itself), are read off
 * its descendants with `has-*`: whichever part holds keyboard focus rings the
 * whole box, and an `aria-invalid` part (the one `FormControl` stamps) turns
 * the whole border red. The parts themselves paint neither, so a focused part
 * never draws a second ring inside the first.
 */
export const mergedControlClasses = [
    "flex w-full items-stretch rounded-control",
    "border border-border-strong bg-surface shadow-input",
    "text-sm text-primary",
    "transition-colors",
    controlHeightClass,
    "has-focus-visible:shadow-focus",
    "has-aria-invalid:border-danger",
].join(" ");
/** The text part of a merged box: the padding a plain `Input` has, no skin. */
export const mergedControlTextClasses = [
    "min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-primary",
    "placeholder:text-placeholder",
    "focus-visible:outline-none",
    "disabled:cursor-not-allowed",
].join(" ");
/**
 * The trigger part of a merged box: no border, no floor, no vertical padding —
 * it stretches to the box instead. A faint hover/focus fill is its only own
 * state, so a keyboard user can still tell WHICH of the two parts the box's
 * single focus ring belongs to.
 */
export const mergedControlTriggerClasses = [
    "flex shrink-0 items-center self-stretch border-0 bg-transparent py-0 text-sm text-primary",
    "rounded-control hover:bg-surface-hover",
    "focus-visible:bg-surface-hover focus-visible:outline-none",
    "disabled:cursor-not-allowed disabled:hover:bg-transparent",
].join(" ");
export const Input = forwardRef(function Input({ className, type = "text", width, ...props }, ref) {
    return (_jsx("input", { ref: ref, type: type, className: cn(controlClasses, fieldWidthClass(width), "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-primary", className), ...props }));
});
Input.displayName = "Input";
//# sourceMappingURL=input.js.map